import { useState, KeyboardEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { X, Clock, Calendar, Search, Filter, Globe, AlertTriangle } from 'lucide-react';
import { useOrganizationPolicies } from '@/hooks/useDNSPolicies';

const DOMAIN_REGEX = /^(\*\.)?([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

function isValidDomain(value: string): boolean {
  return DOMAIN_REGEX.test(value.trim().toLowerCase());
}

type ScheduleType = 'always' | 'scheduled' | 'disabled';
type WeekDay = 0 | 1 | 2 | 3 | 4 | 5 | 6;

const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

interface PolicyFormProps {
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
  initialData?: any;
  isLoading?: boolean;
}

export default function PolicyForm({
  onSubmit,
  onCancel,
  initialData,
  isLoading
}: PolicyFormProps) {
  const { data: existingPolicies = [] } = useOrganizationPolicies();
  const existingDefault = existingPolicies.find(
    p => p.is_default && p.id !== initialData?.id
  );

  const [domainInput, setDomainInput] = useState('');
  const [domainInputError, setDomainInputError] = useState('');
  const [blockedDomains, setBlockedDomains] = useState<string[]>(
    initialData?.policy_domains
      ?.filter((pd: any) => pd.action === 'block')
      .map((pd: any) => pd.domain as string) || []
  );

  const addDomain = (raw: string) => {
    const value = raw.trim().toLowerCase().replace(/\/$/, '');
    if (!value) return;
    if (!isValidDomain(value)) {
      setDomainInputError(`"${value}" is not a valid domain (e.g. instagram.com)`);
      return;
    }
    if (blockedDomains.includes(value)) {
      setDomainInputError(`"${value}" is already in the list`);
      return;
    }
    setBlockedDomains(prev => [...prev, value]);
    setDomainInput('');
    setDomainInputError('');
  };

  const removeDomain = (domain: string) => {
    setBlockedDomains(prev => prev.filter(d => d !== domain));
  };

  const handleDomainKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addDomain(domainInput);
    } else if (e.key === 'Backspace' && domainInput === '' && blockedDomains.length > 0) {
      setBlockedDomains(prev => prev.slice(0, -1));
    }
    setDomainInputError('');
  };

  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    description: initialData?.description || '',
    block_social_media: initialData?.block_social_media || false,
    block_adult_content: initialData?.block_adult_content || false,
    block_gaming: initialData?.block_gaming || false,
    block_streaming: initialData?.block_streaming || false,
    block_news: initialData?.block_news || false,
    allow_bypass_requests: initialData?.allow_bypass_requests || false,
    is_default: initialData?.is_default || false,
    enable_search_filtering: initialData?.enable_search_filtering || false,
    blocked_keywords: initialData?.blocked_keywords || '',
    // Scheduling fields
    schedule_type: (initialData?.schedule_type as ScheduleType) || 'always',
    start_time: initialData?.start_time || '09:00',
    end_time: initialData?.end_time || '17:00',
    days_of_week: initialData?.days_of_week || [] as WeekDay[],
    timezone: initialData?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Policy name is required';
    }

    const hasCategories = formData.block_social_media ||
                         formData.block_adult_content ||
                         formData.block_gaming ||
                         formData.block_streaming ||
                         formData.block_news;

    if (!hasCategories && blockedDomains.length === 0) {
      newErrors.targets = 'Please select at least one category or add at least one domain';
    }

    if (domainInput.trim()) {
      newErrors.domainInput = 'Press Enter or comma to add the domain before saving';
    }

    // Schedule validation
    if (formData.schedule_type === 'scheduled') {
      if (!formData.start_time) {
        newErrors.start_time = 'Start time is required for scheduled policies';
      }
      if (!formData.end_time) {
        newErrors.end_time = 'End time is required for scheduled policies';
      }
      if (formData.days_of_week.length === 0) {
        newErrors.days_of_week = 'Please select at least one day';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validate()) {
      const submitData = {
        ...formData,
        is_active: true,
        custom_domains: blockedDomains.join(','),
        start_time: formData.schedule_type === 'scheduled' ? formData.start_time : null,
        end_time: formData.schedule_type === 'scheduled' ? formData.end_time : null,
        days_of_week: formData.schedule_type === 'scheduled' ? formData.days_of_week : null,
        timezone: formData.schedule_type === 'scheduled' ? formData.timezone : 'UTC',
      };
      await onSubmit(submitData);
    }
  };

  const toggleDay = (day: WeekDay) => {
    setFormData(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day].sort((a, b) => a - b)
    }));
  };

  const removeDay = (day: WeekDay) => {
    setFormData(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.filter(d => d !== day)
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-1">
      <div className="space-y-2">
        <Label htmlFor="name" className="text-[#F3F6FA]">Policy Name</Label>
        <Input
          id="name"
          type="text"
          placeholder="My Custom Policy"
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          className={`bg-[#0A0F0D] border-[#00F5A0]/20 text-[#F3F6FA] placeholder:text-[#F3F6FA]/60 ${errors.name ? 'border-[#0AEFFF]' : ''}`}
        />
        {errors.name && <p className="text-[#0AEFFF] text-sm">{errors.name}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="description" className="text-[#F3F6FA]">Description</Label>
        <Input
          id="description"
          type="text"
          placeholder="Describe what this policy does"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          className="bg-[#0A0F0D] border-[#00F5A0]/20 text-[#F3F6FA] placeholder:text-[#F3F6FA]/60"
        />
      </div>

      <div className="space-y-3">
        <Label className="text-[#F3F6FA]">Block Categories</Label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center space-x-3">
            <Checkbox
              id="social_media"
              checked={formData.block_social_media}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, block_social_media: checked as boolean }))}
              className="data-[state=checked]:bg-[#00F5A0] data-[state=checked]:border-[#00F5A0] border-[#00F5A0]/30"
            />
            <Label htmlFor="social_media" className="text-sm font-normal text-[#F3F6FA] cursor-pointer">
              Social Media
            </Label>
          </div>

          <div className="flex items-center space-x-3">
            <Checkbox
              id="adult_content"
              checked={formData.block_adult_content}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, block_adult_content: checked as boolean }))}
              className="data-[state=checked]:bg-[#00F5A0] data-[state=checked]:border-[#00F5A0] border-[#00F5A0]/30"
            />
            <Label htmlFor="adult_content" className="text-sm font-normal text-[#F3F6FA] cursor-pointer">
              Adult Content
            </Label>
          </div>

          <div className="flex items-center space-x-3">
            <Checkbox
              id="gaming"
              checked={formData.block_gaming}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, block_gaming: checked as boolean }))}
              className="data-[state=checked]:bg-[#00F5A0] data-[state=checked]:border-[#00F5A0] border-[#00F5A0]/30"
            />
            <Label htmlFor="gaming" className="text-sm font-normal text-[#F3F6FA] cursor-pointer">
              Gaming
            </Label>
          </div>

          <div className="flex items-center space-x-3">
            <Checkbox
              id="streaming"
              checked={formData.block_streaming}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, block_streaming: checked as boolean }))}
              className="data-[state=checked]:bg-[#00F5A0] data-[state=checked]:border-[#00F5A0] border-[#00F5A0]/30"
            />
            <Label htmlFor="streaming" className="text-sm font-normal text-[#F3F6FA] cursor-pointer">
              Streaming
            </Label>
          </div>

          <div className="flex items-center space-x-3">
            <Checkbox
              id="news"
              checked={formData.block_news}
              onCheckedChange={(checked) => setFormData(prev => ({ ...prev, block_news: checked as boolean }))}
              className="data-[state=checked]:bg-[#00F5A0] data-[state=checked]:border-[#00F5A0] border-[#00F5A0]/30"
            />
            <Label htmlFor="news" className="text-sm font-normal text-[#F3F6FA] cursor-pointer">
              News
            </Label>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-[#F3F6FA] flex items-center gap-2">
          <Globe className="h-4 w-4 text-[#00F5A0]" />
          Custom Domains to Block
        </Label>
        <p className="text-xs text-[#F3F6FA]/60">
          Type a domain and press <kbd className="px-1 py-0.5 rounded bg-[#00F5A0]/10 text-[#00F5A0] font-mono text-xs">Enter</kbd> or <kbd className="px-1 py-0.5 rounded bg-[#00F5A0]/10 text-[#00F5A0] font-mono text-xs">,</kbd> to add it.
        </p>

        {/* Tag display + input in one box */}
        <div
          className={`flex flex-wrap gap-2 p-2 rounded-md border bg-[#0A0F0D] min-h-[44px] cursor-text
            ${domainInputError || errors.targets ? 'border-red-500' : 'border-[#00F5A0]/30 focus-within:border-[#00F5A0]'}`}
          onClick={() => document.getElementById('domain-tag-input')?.focus()}
        >
          {blockedDomains.map(domain => (
            <Badge
              key={domain}
              className="bg-[#00F5A0]/15 text-[#00F5A0] border border-[#00F5A0]/30 hover:bg-[#00F5A0]/25 gap-1 pr-1"
            >
              {domain}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeDomain(domain); }}
                className="ml-0.5 rounded hover:text-white"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <input
            id="domain-tag-input"
            type="text"
            value={domainInput}
            onChange={(e) => { setDomainInput(e.target.value); setDomainInputError(''); }}
            onKeyDown={handleDomainKeyDown}
            onBlur={() => { if (domainInput.trim()) addDomain(domainInput); }}
            placeholder={blockedDomains.length === 0 ? 'instagram.com' : ''}
            className="flex-1 min-w-[140px] bg-transparent outline-none text-sm text-[#F3F6FA] placeholder:text-[#F3F6FA]/40"
          />
        </div>

        {domainInputError && <p className="text-red-400 text-xs">{domainInputError}</p>}
        {errors.domainInput && <p className="text-yellow-400 text-xs">{errors.domainInput}</p>}
        {errors.targets && <p className="text-[#0AEFFF] text-sm">{errors.targets}</p>}
      </div>

      {/* Search Filtering Section */}
      <div className="space-y-4 border-t border-[#00F5A0]/20 pt-6">
        <div className="flex items-center space-x-2 mb-4">
          <Search className="h-4 w-4 text-[#00F5A0]" />
          <Label className="text-[#F3F6FA] font-semibold">Search Query Filtering</Label>
        </div>

        <div className="flex items-center space-x-3">
          <Checkbox
            id="enable_search_filtering"
            checked={formData.enable_search_filtering}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, enable_search_filtering: checked as boolean }))}
            className="data-[state=checked]:bg-[#00F5A0] data-[state=checked]:border-[#00F5A0] border-[#00F5A0]/30"
          />
          <Label htmlFor="enable_search_filtering" className="text-sm font-normal text-[#F3F6FA] cursor-pointer">
            Enable search query filtering
          </Label>
        </div>

      </div>

      {/* Keyword Management Section */}
      {formData.enable_search_filtering && (
        <div className="space-y-4 border-t border-[#00F5A0]/20 pt-6">
          <div className="flex items-center space-x-2 mb-4">
            <Filter className="h-4 w-4 text-[#00F5A0]" />
            <Label className="text-[#F3F6FA] font-semibold">Blocked Keywords</Label>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="blocked_keywords" className="text-[#F3F6FA]">
              Keywords to Block
            </Label>
            <p className="text-xs text-[#F3F6FA]/60">
              Enter keywords separated by commas. Examples: inappropriate, explicit, violence
            </p>
            <Textarea
              id="blocked_keywords"
              placeholder="Enter keywords to block here...&#10;inappropriate, explicit, violence"
              value={formData.blocked_keywords}
              onChange={(e) => setFormData(prev => ({ ...prev, blocked_keywords: e.target.value }))}
              className="bg-[#0A0F0D] border-[#00F5A0]/30 text-[#F3F6FA] placeholder:text-[#F3F6FA]/60 min-h-[100px] resize-vertical focus:border-[#00F5A0] focus:ring-1 focus:ring-[#00F5A0]/50"
              rows={4}
            />
            <p className="text-xs text-[#F3F6FA]/60 mt-1">
              Keywords will be blocked in search queries when this policy is active. 
              Matching is case-insensitive and will block queries containing these keywords.
            </p>
          </div>
        </div>
      )}

      <div className="space-y-4 border-t border-[#00F5A0]/20 pt-6">
        <div className="flex items-center space-x-2 mb-4">
          <Clock className="h-4 w-4 text-[#00F5A0]" />
          <Label className="text-[#F3F6FA] font-semibold">Schedule Settings</Label>
        </div>

        <div className="space-y-2">
          <Label htmlFor="schedule_type" className="text-[#F3F6FA]">Policy Schedule</Label>
          <Select value={formData.schedule_type} onValueChange={(value: ScheduleType) => setFormData(prev => ({ ...prev, schedule_type: value }))}>
            <SelectTrigger className="bg-[#0A0F0D] border-[#00F5A0]/20 text-[#F3F6FA]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0F0D] border-[#00F5A0]/20">
              <SelectItem value="always" className="text-[#F3F6FA] focus:bg-[#00F5A0]/10">
                Always Active (24/7 blocking)
              </SelectItem>
              <SelectItem value="scheduled" className="text-[#F3F6FA] focus:bg-[#00F5A0]/10">
                Scheduled (Time-based blocking)
              </SelectItem>
              <SelectItem value="disabled" className="text-[#F3F6FA] focus:bg-[#00F5A0]/10">
                Disabled (Policy inactive)
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {formData.schedule_type === 'scheduled' && (
          <div className="space-y-4 p-4 bg-[#00F5A0]/5 rounded-lg border border-[#00F5A0]/20">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start_time" className="text-[#F3F6FA]">Start Time</Label>
                <Input
                  id="start_time"
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                  className={`bg-[#0A0F0D] border-[#00F5A0]/20 text-[#F3F6FA] ${errors.start_time ? 'border-[#0AEFFF]' : ''}`}
                />
                {errors.start_time && <p className="text-[#0AEFFF] text-sm">{errors.start_time}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="end_time" className="text-[#F3F6FA]">End Time</Label>
                <Input
                  id="end_time"
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                  className={`bg-[#0A0F0D] border-[#00F5A0]/20 text-[#F3F6FA] ${errors.end_time ? 'border-[#0AEFFF]' : ''}`}
                />
                {errors.end_time && <p className="text-[#0AEFFF] text-sm">{errors.end_time}</p>}
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-[#F3F6FA] flex items-center space-x-2">
                <Calendar className="h-4 w-4" />
                <span>Days of Week</span>
              </Label>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {WEEKDAY_OPTIONS.map((day) => (
                  <div key={day.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`day-${day.value}`}
                      checked={formData.days_of_week.includes(day.value as WeekDay)}
                      onCheckedChange={() => toggleDay(day.value as WeekDay)}
                      className="data-[state=checked]:bg-[#00F5A0] data-[state=checked]:border-[#00F5A0] border-[#00F5A0]/30"
                    />
                    <Label htmlFor={`day-${day.value}`} className="text-sm text-[#F3F6FA] cursor-pointer">
                      {day.label}
                    </Label>
                  </div>
                ))}
              </div>
              
              {formData.days_of_week.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.days_of_week.map((day) => {
                    const dayOption = WEEKDAY_OPTIONS.find(d => d.value === day);
                    return (
                      <Badge 
                        key={day} 
                        className="bg-[#00F5A0]/20 text-[#00F5A0] border-[#00F5A0]/30 hover:bg-[#00F5A0]/30"
                      >
                        {dayOption?.label}
                        <X 
                          className="h-3 w-3 ml-1 cursor-pointer hover:text-[#F3F6FA]" 
                          onClick={() => removeDay(day)}
                        />
                      </Badge>
                    );
                  })}
                </div>
              )}
              
              {errors.days_of_week && <p className="text-[#0AEFFF] text-sm">{errors.days_of_week}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="timezone" className="text-[#F3F6FA]">Timezone</Label>
              <Input
                id="timezone"
                type="text"
                value={formData.timezone}
                onChange={(e) => setFormData(prev => ({ ...prev, timezone: e.target.value }))}
                className="bg-[#0A0F0D] border-[#00F5A0]/20 text-[#F3F6FA] placeholder:text-[#F3F6FA]/60"
                placeholder="UTC"
              />
              <p className="text-xs text-[#F3F6FA]/60">
                Current timezone: {Intl.DateTimeFormat().resolvedOptions().timeZone}
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="space-y-3 border-t border-[#00F5A0]/20 pt-6">
        <div className="flex items-center space-x-3">
          <Checkbox
            id="is_default"
            checked={formData.is_default}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_default: checked as boolean }))}
            className="data-[state=checked]:bg-[#00F5A0] data-[state=checked]:border-[#00F5A0] border-[#00F5A0]/30"
          />
          <div>
            <Label htmlFor="is_default" className="text-sm font-normal text-[#F3F6FA] cursor-pointer">
              Set as default policy
            </Label>
            <p className="text-xs text-[#F3F6FA]/50 mt-0.5">
              The default policy is always active. Your DNS server uses this when no schedule applies.
            </p>
            {formData.is_default && existingDefault && (
              <p className="flex items-center gap-1 text-xs text-yellow-400 mt-1">
                <AlertTriangle className="h-3 w-3 shrink-0" />
                This will replace <span className="font-semibold">"{existingDefault.name}"</span> as the default.
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <Checkbox
            id="allow_bypass"
            checked={formData.allow_bypass_requests}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, allow_bypass_requests: checked as boolean }))}
            className="data-[state=checked]:bg-[#00F5A0] data-[state=checked]:border-[#00F5A0] border-[#00F5A0]/30"
          />
          <Label htmlFor="allow_bypass" className="text-sm font-normal text-[#F3F6FA] cursor-pointer">
            Allow bypass requests (users can request to unblock sites)
          </Label>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 pt-4">
        <Button 
          type="button" 
          variant="outline" 
          onClick={onCancel} 
          className="border-[#00F5A0]/20 text-[#F3F6FA] hover:bg-[#00F5A0]/10"
        >
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={isLoading} 
          className="bg-gradient-to-r from-[#00F5A0] to-[#0AEFFF] text-[#0A0F0D] font-semibold"
        >
          {isLoading ? 'Saving...' : 'Save Policy'}
        </Button>
      </div>
    </form>
  );
}